/**
 * DriverSettingsPage - Pagina de Configuracoes do Motorista
 *
 * Funcionalidades:
 * - Configuracoes do app
 * - Informacoes do servidor
 * - Versao do app
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  LocationOn as LocationIcon,
  DarkMode as DarkModeIcon,
  Language as LanguageIcon,
  Storage as ServerIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
  Delete as DeleteIcon,
  ChevronRight as ArrowIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useTranslation } from '../common/components/LocalizationProvider';

const SettingItem = ({
  icon: Icon, label, description, value, onChange, type = 'switch', onClick, tokens, color,
}) => (
  <ListItem
    component={onClick ? 'button' : 'div'}
    onClick={onClick}
    sx={{
      py: 1.5,
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': onClick ? { backgroundColor: tokens?.surface?.elevated } : {},
    }}
  >
    <ListItemIcon sx={{ minWidth: 44 }}>
      <Icon sx={{ color: color || tokens?.text?.secondary }} />
    </ListItemIcon>
    <ListItemText
      primary={label}
      secondary={description}
      primaryTypographyProps={{ fontSize: '15px', color: tokens?.text?.primary }}
      secondaryTypographyProps={{ fontSize: '12px', color: tokens?.text?.tertiary }}
    />
    <ListItemSecondaryAction>
      {type === 'switch' ? (
        <Switch
          checked={value}
          onChange={(e) => onChange?.(e.target.checked)}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: tokens?.accent?.primary },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: tokens?.accent?.primary,
            },
          }}
        />
      ) : (
        <ArrowIcon sx={{ color: tokens?.text?.tertiary }} />
      )}
    </ListItemSecondaryAction>
  </ListItem>
);

const DriverSettingsPage = () => {
  const t = useTranslation();
  const tokens = usePremiumTokens();

  // Settings state
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [locationEnabled, setLocationEnabled] = useState(true);
  const [darkMode, setDarkMode] = useState(false);

  // Dialogs
  const [serverInfoOpen, setServerInfoOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [clearDataOpen, setClearDataOpen] = useState(false);

  // Server info
  const serverUrl = window.location.origin;

  const handleClearData = () => {
    // Clear local storage
    localStorage.clear();
    sessionStorage.clear();
    setClearDataOpen(false);
    window.location.reload();
  };

  return (
    <Box sx={{ p: 2, pb: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <SettingsIcon sx={{ color: tokens?.accent?.primary }} />
        <Typography
          sx={{
            color: tokens?.text?.primary,
            fontWeight: 700,
            fontSize: '20px',
          }}
        >
          Configuracoes
        </Typography>
      </Box>

      {/* Notificacoes e Localizacao */}
      <Typography
        sx={{
          color: tokens?.text?.tertiary,
          fontSize: '12px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          mb: 1,
          px: 1,
        }}
      >
        Permissoes
      </Typography>
      <Card
        sx={{
          mb: 3,
          backgroundColor: tokens?.surface?.raised,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${tokens?.border?.subtle}`,
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <List disablePadding>
            <SettingItem
              icon={NotificationsIcon}
              label="Notificacoes"
              description="Receber alertas de novas rotas"
              value={notificationsEnabled}
              onChange={setNotificationsEnabled}
              tokens={tokens}
            />
            <Divider sx={{ borderColor: tokens?.border?.subtle }} />
            <SettingItem
              icon={LocationIcon}
              label="Localizacao"
              description="Compartilhar posicao durante rotas"
              value={locationEnabled}
              onChange={setLocationEnabled}
              tokens={tokens}
            />
          </List>
        </CardContent>
      </Card>

      {/* Aparencia */}
      <Typography
        sx={{
          color: tokens?.text?.tertiary,
          fontSize: '12px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          mb: 1,
          px: 1,
        }}
      >
        Aparencia
      </Typography>
      <Card
        sx={{
          mb: 3,
          backgroundColor: tokens?.surface?.raised,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${tokens?.border?.subtle}`,
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <List disablePadding>
            <SettingItem
              icon={DarkModeIcon}
              label="Modo escuro"
              description="Usar tema escuro"
              value={darkMode}
              onChange={setDarkMode}
              tokens={tokens}
            />
          </List>
        </CardContent>
      </Card>

      {/* Informacoes */}
      <Typography
        sx={{
          color: tokens?.text?.tertiary,
          fontSize: '12px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          mb: 1,
          px: 1,
        }}
      >
        Informacoes
      </Typography>
      <Card
        sx={{
          mb: 3,
          backgroundColor: tokens?.surface?.raised,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${tokens?.border?.subtle}`,
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <List disablePadding>
            <SettingItem
              icon={ServerIcon}
              label="Servidor"
              description={serverUrl}
              type="action"
              onClick={() => setServerInfoOpen(true)}
              tokens={tokens}
            />
            <Divider sx={{ borderColor: tokens?.border?.subtle }} />
            <SettingItem
              icon={InfoIcon}
              label="Sobre o app"
              description="Versao e informacoes"
              type="action"
              onClick={() => setAboutOpen(true)}
              tokens={tokens}
            />
            <Divider sx={{ borderColor: tokens?.border?.subtle }} />
            <SettingItem
              icon={SecurityIcon}
              label="Politica de privacidade"
              description="Como seus dados sao usados"
              type="action"
              onClick={() => window.open('/privacy-policy', '_blank')}
              tokens={tokens}
            />
          </List>
        </CardContent>
      </Card>

      {/* Dados */}
      <Typography
        sx={{
          color: tokens?.text?.tertiary,
          fontSize: '12px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          mb: 1,
          px: 1,
        }}
      >
        Dados
      </Typography>
      <Card
        sx={{
          backgroundColor: tokens?.surface?.raised,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${tokens?.border?.subtle}`,
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <List disablePadding>
            <SettingItem
              icon={DeleteIcon}
              label="Limpar dados locais"
              description="Remove cache e configuracoes"
              type="action"
              onClick={() => setClearDataOpen(true)}
              tokens={tokens}
              color={tokens?.semantic?.error}
            />
          </List>
        </CardContent>
      </Card>

      {/* Server Info Dialog */}
      <Dialog
        open={serverInfoOpen}
        onClose={() => setServerInfoOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: tokens?.surface?.raised,
            borderRadius: tokens?.radius?.xl || '16px',
          },
        }}
      >
        <DialogTitle sx={{ color: tokens?.text?.primary }}>
          Informacoes do Servidor
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px' }}>
                URL do Servidor
              </Typography>
              <Typography
                sx={{
                  color: tokens?.text?.primary,
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  backgroundColor: tokens?.surface?.elevated,
                  p: 1,
                  borderRadius: 1,
                  mt: 0.5,
                }}
              >
                {serverUrl}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px' }}>
                Status
              </Typography>
              <Typography sx={{ color: tokens?.semantic?.success, fontSize: '14px' }}>
                Conectado
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setServerInfoOpen(false)} variant="contained">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* About Dialog */}
      <Dialog
        open={aboutOpen}
        onClose={() => setAboutOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: tokens?.surface?.raised,
            borderRadius: tokens?.radius?.xl || '16px',
          },
        }}
      >
        <DialogTitle sx={{ color: tokens?.text?.primary }}>
          Sobre o Fleet Driver
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '20px',
                backgroundColor: tokens?.accent?.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <Typography sx={{ color: 'white', fontSize: '32px', fontWeight: 700 }}>
                FD
              </Typography>
            </Box>
            <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600, fontSize: '18px' }}>
              Fleet Driver
            </Typography>
            <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '14px' }}>
              Versao 1.0.0 (PWA)
            </Typography>
            <Divider sx={{ my: 2, borderColor: tokens?.border?.subtle }} />
            <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px' }}>
              App para motoristas do Fleet Premium.
              Gerencie suas rotas e entregas de forma simples e eficiente.
            </Typography>
            <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px', mt: 2 }}>
              2024 Fleet Premium - Todos os direitos reservados
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAboutOpen(false)} variant="contained">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Data Dialog */}
      <Dialog
        open={clearDataOpen}
        onClose={() => setClearDataOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: tokens?.surface?.raised,
            borderRadius: tokens?.radius?.xl || '16px',
          },
        }}
      >
        <DialogTitle sx={{ color: tokens?.text?.primary }}>
          Limpar dados locais?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: tokens?.text?.secondary }}>
            Isso ira remover todo o cache e configuracoes salvas localmente.
            Voce precisara fazer login novamente.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setClearDataOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleClearData} variant="contained" color="error">
            Limpar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DriverSettingsPage;
